import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    Active,
    Collision,
    CollisionDetection,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    MouseSensor,
    Over,
    pointerWithin,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import isEqual from 'lodash/isEqual'

import { useAppContext } from 'app/useAppContext'
import { useAppUserContext } from 'app/useAppUserContext'
import { useCreateNavigation, useNavigation, useUpdateNavigation } from 'data/hooks/navigation'
import { usePages } from 'data/hooks/pages'
import { useViews } from 'data/hooks/views'
import { buildNavTree } from 'features/admin/settings/navigation/NavigationUtils'
import { NAVIGATION_ROOT_DROP_ID } from 'features/navigation/NavigationEditor/constants'
import { NavigationAppMenuItem, NavigationAppMenuItemView } from 'features/navigation/types'
import { mapNavTreeToAppNavigationMenuItems } from 'features/navigation/utils'

import { useToast } from 'ui/components/Toast'

export function useNavigationEditorState() {
    const { selectedStack } = useAppContext()

    const { mutateAsync: updateNavigation } = useUpdateNavigation()
    const { data: navigation = [], isFetched } = useNavigation()

    const [localNavigation, setLocalNavigation] = useState(navigation as NavigationDto[])
    const localNavigationRef = useRef(localNavigation)
    localNavigationRef.current = localNavigation

    // Update the local navigation when the navigation changes.
    useEffect(() => {
        const prevNavigation = localNavigationRef.current
        const newNavigation = navigation as NavigationDto[]

        if (!isEqual(prevNavigation, newNavigation)) {
            setLocalNavigation(navigation as NavigationDto[])
        }
    }, [navigation])

    const { data: views = [] } = useViews()
    const { data: pages = [] } = usePages()
    const { role } = useAppUserContext()

    const menuItems = useMemo(() => {
        const navTree = buildNavTree(
            localNavigation,
            views,
            pages as PageDto[],
            role,
            selectedStack?._sid,
            true
        )

        return mapNavTreeToAppNavigationMenuItems(
            navTree,
            undefined,
            selectedStack ?? undefined,
            true
        )
    }, [localNavigation, pages, role, selectedStack, views])

    const homepageItemSid = findHomepageItemSid(menuItems)

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                // This lets us select items on click, but still keeps the dragging functionality working in the same way.
                distance: 5,
            },
        })
    )

    const toast = useToast()
    const updateNavItem = useCallback(
        async (sid: string, patch: Partial<NavigationDto>) => {
            try {
                await updateNavigation({
                    id: sid,
                    patch: patch,
                })
            } catch {
                toast({
                    type: 'error',
                    title: 'There was a problem updating the navigation item',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            }
        },
        [updateNavigation, toast]
    )

    const updateDragItems = useCallback(
        (active: Active, over: Over | null, handleIdxChange: boolean) => {
            if (!over) return

            const navigationItems = localNavigationRef.current
            const activeId = active.id as string

            // Get source list info
            const activeDroppableId = active.data.current?.sortable?.containerId
            const activeItems = (active.data.current?.sortable?.items as string[]) ?? []
            const activeIdx = active.data.current?.sortable?.index

            // Get target list info
            const overNavItem = navigationItems.find((item) => item._sid === over.id)
            const isOverFolder = overNavItem?.options?.is_folder

            // If dropping on a folder, use folder as target container
            const overDroppableId = isOverFolder
                ? overNavItem._sid
                : over.data.current?.sortable?.containerId
            const overItems = isOverFolder
                ? navigationItems.filter((i) => i.parent_id === overNavItem._sid).map((i) => i._sid)
                : ((over.data.current?.sortable?.items as string[]) ?? [])
            const overIdx = isOverFolder
                ? overItems.findIndex((i) => i === overDroppableId) + 1
                : over.data.current?.sortable?.index

            // Prevent dropping folder into itself
            if (activeId === overDroppableId) return

            setLocalNavigation((prev) => {
                // Case 1: Reordering within same container
                if (activeDroppableId === overDroppableId) {
                    if (!handleIdxChange) return prev

                    // Create map of new sort orders after moving item
                    const sortedItems = arrayMove(activeItems, activeIdx, overIdx)
                    const sortOrders = new Map(sortedItems.map((item, i) => [item, i]))

                    return prev.map((item) => {
                        const newOrder = sortOrders.get(item._sid)
                        // Skip if item not in container or order unchanged
                        if (newOrder === undefined || item.display_order === newOrder + 1) {
                            return item
                        }
                        return { ...item, display_order: newOrder + 1 }
                    })
                }

                // Case 2: Moving between containers
                // Remove item from source list and get new sort orders
                const sourceItems = activeItems.filter((id) => id !== activeId)
                const sourceSortOrders = new Map(sourceItems.map((item, i) => [item, i]))

                // Insert item into target list and get new sort orders
                const targetItems = [...overItems]
                targetItems.splice(overIdx, 0, activeId)
                const targetSortOrders = new Map(targetItems.map((item, i) => [item, i]))

                return prev.map((item) => {
                    // Update item being dragged
                    if (item._sid === activeId) {
                        return {
                            ...item,
                            parent_id:
                                overDroppableId === NAVIGATION_ROOT_DROP_ID
                                    ? null
                                    : overDroppableId,
                            display_order: targetSortOrders.get(item._sid)! + 1,
                        }
                    }

                    // Update items in source container
                    const sourceOrder = sourceSortOrders.get(item._sid)
                    if (sourceOrder !== undefined && item.display_order !== sourceOrder + 1) {
                        return { ...item, display_order: sourceOrder + 1 }
                    }

                    // Update items in target container
                    const targetOrder = targetSortOrders.get(item._sid)
                    if (targetOrder !== undefined && item.display_order !== targetOrder + 1) {
                        return { ...item, display_order: targetOrder + 1 }
                    }

                    return item
                })
            })
        },
        []
    )

    const [activeDragItemId, setActiveDragItemId] = useState<string | undefined>()

    const originalNavigationRef = useRef(localNavigation)

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const { active } = event
        setActiveDragItemId(active.id as string)
        originalNavigationRef.current = localNavigationRef.current
    }, [])

    const handleDragOver = useCallback(
        (event: DragOverEvent) => {
            const { active, over } = event

            requestAnimationFrame(() => {
                updateDragItems(active, over, false)
            })
        },
        [updateDragItems]
    )

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event

            const originalItems = originalNavigationRef.current
            const originalItemsBySid = new Map(originalItems.map((item) => [item._sid, item]))

            requestAnimationFrame(() => {
                updateDragItems(active, over, true)
                setActiveDragItemId(undefined)
                originalNavigationRef.current = []

                queueMicrotask(() => {
                    const updates: Promise<void>[] = []
                    const newItems = localNavigationRef.current

                    for (const item of newItems) {
                        const originalItem = originalItemsBySid.get(item._sid)
                        // If the item has been moved, update it.
                        if (
                            originalItem?.display_order !== item.display_order ||
                            originalItem?.parent_id !== item.parent_id
                        ) {
                            updates.push(updateNavItem(item._sid, item))
                        }
                    }

                    Promise.all(updates)
                })
            })
        },
        [updateDragItems, updateNavItem]
    )

    const handleDragCancel = useCallback(() => {
        setLocalNavigation(originalNavigationRef.current)
        setActiveDragItemId(undefined)
        originalNavigationRef.current = []
    }, [])

    const collisionDetection: CollisionDetection = useCallback((args) => {
        const { active } = args

        const activeDroppableId = active.data.current?.sortable?.containerId

        const activeItemId = active.id as string

        const result = pointerWithin(args)

        return getSupportedCollisions(
            result,
            activeItemId,
            activeDroppableId,
            localNavigationRef.current
        )
    }, [])

    const isDragging = !!activeDragItemId

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        // Prevent closing the settings modal when the escape key is pressed.
        if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
        }
    }, [])

    const isEmpty = isFetched && !menuItems.length

    const [editingItemId, setEditingItemId] = useState<string | undefined>()
    // If we're dragging an item, don't allow editing.
    useEffect(() => {
        if (isDragging) {
            setEditingItemId(undefined)
        }
    }, [isDragging])

    const lastItemDisplayOrder = menuItems[menuItems.length - 1]?.displayOrder ?? -1

    const { mutateAsync: createNavigation } = useCreateNavigation()

    const createFolder = useCallback(async () => {
        const displayOrder = lastItemDisplayOrder + 1

        const newItem = await createNavigation({
            label: 'New folder',
            url: '',
            parent_id: null,
            object_id: null,
            display_order: displayOrder,
            options: {
                is_folder: true,
            },
        } as NavigationDto)

        setEditingItemId(newItem._sid)
    }, [createNavigation, lastItemDisplayOrder, setEditingItemId])

    const createSeparator = useCallback(async () => {
        const displayOrder = lastItemDisplayOrder + 1

        const newItem = await createNavigation({
            label: 'New separator',
            url: '',
            parent_id: null,
            object_id: null,
            display_order: displayOrder,
            options: {
                is_separator: true,
            },
        } as NavigationDto)

        setEditingItemId(newItem._sid)
    }, [createNavigation, lastItemDisplayOrder, setEditingItemId])

    return useMemo(
        () => ({
            menuItems,
            homepageItemSid,
            sensors,
            handleDragStart,
            handleDragEnd,
            handleDragOver,
            handleDragCancel,
            isDragging,
            collisionDetection,
            handleKeyDown,
            isEmpty,
            createFolder,
            createSeparator,
            editingItemId,
            setEditingItemId,
        }),
        [
            menuItems,
            homepageItemSid,
            sensors,
            handleDragStart,
            handleDragEnd,
            handleDragOver,
            handleDragCancel,
            isDragging,
            collisionDetection,
            handleKeyDown,
            isEmpty,
            createFolder,
            createSeparator,
            editingItemId,
        ]
    )
}

function findHomepageItemSid(menuItems: NavigationAppMenuItem[]): string | undefined {
    for (const item of menuItems) {
        if (item.type !== 'separator' && !item.hidden && item.children.length > 0) {
            const homepageItemSid = findHomepageItemSid(item.children)
            if (homepageItemSid) return homepageItemSid
            continue
        }

        if ((item as NavigationAppMenuItemView).hidden) continue
        if (item.type !== 'view') continue

        return item.id
    }

    return undefined
}

function getSupportedCollisions(
    collisions: Collision[],
    activeItemId: string,
    activeDroppableId: string,
    navigation: NavigationDto[]
) {
    const navigationBySid = new Map(navigation.map((item) => [item._sid, item]))

    return collisions.filter((target) => {
        const activeItem = navigationBySid.get(activeItemId)
        const droppableNavItem = navigationBySid.get(target.id as string)

        let targetDroppableId = target.data?.droppableContainer?.data.current?.sortable?.containerId
        if (droppableNavItem?.options?.is_folder) {
            targetDroppableId = target.id
        }

        const activeDroppableItem = navigationBySid.get(activeDroppableId)

        // Don't allow dragging a folder into another folder.
        if (droppableNavItem?.options?.is_folder && activeItem?.options?.is_folder) {
            return false
        }

        // If we're dragging a root item into a folder or vice versa, allow all collisions.
        if (
            (activeDroppableId === NAVIGATION_ROOT_DROP_ID &&
                droppableNavItem?.options?.is_folder) ||
            (activeDroppableItem?.options?.is_folder &&
                targetDroppableId === NAVIGATION_ROOT_DROP_ID)
        ) {
            return true
        }

        // If we're dragging from a folder into a folder, allow all collisions.
        if (activeDroppableItem?.options?.is_folder && droppableNavItem?.options?.is_folder) {
            return true
        }

        // Can only drag items within the same droppable container.
        if (activeDroppableId !== targetDroppableId) {
            return false
        }

        return true
    })
}
